
import { useTasks } from '@/contexts/task/TaskContext';
import { Task } from '@/types/tasks';
import { createDragStartHandler, createDragOverHandler, createDragEndHandler } from './drag-handlers';
import { updateTaskInDatabase } from './task-database-operations';
import { useTaskStateUpdates } from './use-task-state-updates';
import { useCallback, useMemo, useState } from 'react';

/**
 * Hook that provides drag-and-drop functionality for the task board
 * Now uses immediate local state updates like workbook/storage systems
 */
export const useTaskBoardDrag = (
  setActiveTask: (task: Task | null) => void,
  setShouldUseLocalTasks: (value: boolean) => void
) => {
  const { 
    activeTasks, 
    completedTasks, 
    setActiveTasks, 
    setCompletedTasks
  } = useTasks();
  
  const { isUpdating } = useTaskStateUpdates();
  const [isDragUpdating, setIsDragUpdating] = useState(false);
  
  // Memoize drag handlers to prevent unnecessary re-renders
  const handleDragStart = useCallback((event: any) => {
    // Use the factory-created handler
    createDragStartHandler(
      setActiveTask,
      activeTasks,
      completedTasks
    )(event);
  }, [setActiveTask, activeTasks, completedTasks]);
  
  // Create memoized drag over handler
  const handleDragOver = useMemo(() => 
    createDragOverHandler(activeTasks, completedTasks),
    [activeTasks, completedTasks]
  );
  
  const handleDragEnd = useCallback(async (event: any) => {
    setIsDragUpdating(true);
    
    try {
      // Use the factory-created handler with simple database updates
      await createDragEndHandler(
        setActiveTask,
        activeTasks,
        completedTasks,
        setActiveTasks,
        setCompletedTasks,
        updateTaskInDatabase
      )(event);
    } finally {
      setIsDragUpdating(false);
    }
  }, [
    setActiveTask,
    activeTasks,
    completedTasks,
    setActiveTasks,
    setCompletedTasks
  ]);

  return {
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    isUpdating: isUpdating || isDragUpdating
  };
};
